/**
 * `useBriefSectionViewer` — TipTap `useEditor` wrapper for a brief section's
 * content. Powers BOTH the read-only brief viewer and the editable brief
 * editor.
 *
 * Returns an `Editor` instance for valid TipTap doc content
 * (`{ type: 'doc', content: [...] }`). `null`/`undefined` content is
 * normalized to an empty doc so an editable empty section still mounts an
 * editor (the BE persists `null` for AI-skipped and aiAllowed=false sections).
 * Returns `null` only when content is present but structurally malformed
 * (not an object, or not a doc node); the caller renders the per-section
 * error fallback in that case.
 *
 * **Edit mode:** pass `options.editable` to switch between
 * read-only (`false`, the default, so every existing caller stays read-only)
 * and editable (`true`, used by `BriefSectionEditor`).
 *
 * **TipTap remount strategy (no useEffect):** `useEditor` is called once
 * per mount with the section content as initial input. To force a fresh
 * editor when content changes (e.g. across section IDs in a list), the
 * parent passes `key={section.id}` so React unmounts and remounts the
 * subtree — TipTap's teardown is automatic via `useEditor`. This avoids the
 * `useEffect(() => editor.commands.setContent)` pattern which would violate
 * the project's no-use-effect rule.
 */
import Placeholder from '@tiptap/extension-placeholder';
import { useEditor, type Editor, type EditorOptions } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { i18n } from '@/shared/i18n';
import {
  CommentHighlightExtension,
  ListBoundaryKeymapExtension,
  type CommentHighlightOptions,
} from '@/shared/lib/tiptap';

/**
 * Type alias for the shape `useEditor` accepts as its `content` option.
 * Derived from `EditorOptions['content']` so we stay in lockstep with the
 * installed TipTap version without importing transitive packages.
 */
type EditorContent = EditorOptions['content'];

/**
 * Runtime guard for the shape produced by `wrapInTipTapDoc` on the backend
 * (`apps/api/src/agents/use-cases/brief-generation/brief-generation.agent.ts`):
 * a top-level `doc` node. Brief sections store TipTap content in JSONB, so
 * the at-rest type is `unknown` from the SDK perspective — we narrow here
 * so the editor only ever sees a structurally-valid root document. Inner
 * children are validated by ProseMirror at render time.
 */
function isTipTapDoc(value: unknown): value is EditorContent {
  return (
    typeof value === 'object' &&
    value !== null &&
    'type' in value &&
    (value as { type: unknown }).type === 'doc'
  );
}

/**
 * The BE persists `null` for brief sections that AI was not allowed to fill
 * or that the LLM dropped during generation (see brief-generation.agent.ts).
 * Treat both `null` and `undefined` as "empty doc, mount an empty editor"
 * rather than failing into the error fallback — the user should be able to
 * type into an empty section, not see "Cannot display section".
 */
const EMPTY_TIPTAP_DOC: EditorContent = { type: 'doc', content: [{ type: 'paragraph' }] };

/**
 * A doc with no children is what the BE stores for a section nobody has written yet, and
 * ProseMirror renders it as nothing at all: no textblock, so no line to click into and no
 * node for the placeholder decoration to land on. Seeding one empty paragraph is what makes
 * an untouched section show its prompt and accept a click.
 */
function isChildlessDoc(value: EditorContent): boolean {
  const content = (value as { content?: unknown }).content;
  return !Array.isArray(content) || content.length === 0;
}

function normalizeContent(value: unknown): EditorContent {
  if (value == null) return EMPTY_TIPTAP_DOC;
  if (!isTipTapDoc(value)) return null;
  return isChildlessDoc(value) ? EMPTY_TIPTAP_DOC : value;
}

/**
 * Options for {@link useBriefSectionViewer}.
 *
 * `editable` defaults to `false` so every existing call site
 * (`useBriefSectionViewer(section.content)`) keeps read-only semantics
 * without modification.
 *
 * `onUpdate` is forwarded straight to TipTap's `useEditor({ onUpdate })`
 * so the editor's transaction → update pipeline drives the consumer's
 * onChange handler. Used by `BriefSectionEditor` to lift
 * the document JSON to the parent's brief form state.
 */
export interface UseBriefSectionViewerOptions {
  editable?: boolean;
  onUpdate?: EditorOptions['onUpdate'];
  /** Forwarded to TipTap. Used by the page to track the active editor for the shared toolbar. */
  onFocus?: EditorOptions['onFocus'];
  /** Forwarded to TipTap. Used to detect text selections for the comment highlight flow. */
  onSelectionUpdate?: EditorOptions['onSelectionUpdate'];
  /** Wired into CommentHighlightExtension. Required to receive highlight click events. */
  onCommentClick?: CommentHighlightOptions['onCommentClick'];
  /**
   * Prompt shown while the section is empty. The brief editor passes the section's own
   * description so an empty section states what belongs in it; callers that render a
   * section outside that context fall back to the generic prompt.
   */
  placeholder?: string;
  /**
   * Exposes the editor as a named text field. The brief editor passes the section name;
   * read-only previews omit it and render with no role.
   */
  ariaLabel?: string;
}

export function useBriefSectionViewer(
  content: unknown,
  options?: UseBriefSectionViewerOptions,
): Editor | null {
  const safeContent = normalizeContent(content);
  const editable = options?.editable ?? false;
  const onUpdate = options?.onUpdate;
  const onFocus = options?.onFocus;
  const onSelectionUpdate = options?.onSelectionUpdate;
  const onCommentClick = options?.onCommentClick ?? (() => {});
  const placeholder =
    options?.placeholder ?? i18n.t('briefs:brief.briefSectionViewer.typeYourContentHere');
  const ariaLabel = options?.ariaLabel;

  const editor = useEditor({
    extensions: [
      // `trailingNode` is off so an empty paragraph after a section's terminal
      // list can actually be removed: otherwise TrailingNode re-appends an
      // escape paragraph the instant the section ends in a list, undoing the
      // deletion below.
      StarterKit.configure({ trailingNode: false }),
      // Wins over StarterKit's bundled list-keymap (via higher extension
      // priority) so an empty paragraph next to a list is deleted, not merged.
      ListBoundaryKeymapExtension,
      CommentHighlightExtension.configure({ onCommentClick }),
      Placeholder.configure({ placeholder }),
    ],
    content: safeContent,
    editable,
    onUpdate,
    onFocus,
    onSelectionUpdate,
    // Passing `attributes` at all replaces the `role="textbox"` TipTap adds by default, once
    // the editor settles after mount. A named editor restates the role alongside its name; a
    // read-only preview passes none, so it reads as content rather than an unnamed text field.
    // Once the role is textbox, assistive technology reads editability from aria-readonly
    // rather than contenteditable.
    editorProps: {
      /* eslint-disable i18next/no-literal-string -- ARIA attributes, not copy */
      attributes: ariaLabel
        ? {
            role: 'textbox',
            'aria-multiline': 'true',
            'aria-label': ariaLabel,
            'aria-readonly': String(!editable),
          }
        : {},
      /* eslint-enable i18next/no-literal-string */
    },
  });

  return safeContent ? editor : null;
}
